/**
 * settle_hotspots — R10 (settlement study, 2026-09-02): the pure pieces of
 * the assembly + collider hot-spot work, so the harness pins them.
 *   · SpatialHash2D — the placement overlap test was O(n²) hypot over every
 *     placed footprint (three passes per placement: furniture re-validation,
 *     the block test, the shrink ladder); a 48 m grid makes it O(neighbours).
 *   · colliderRingWant — Rapier BVH / hull builds are DEFERRED to walking
 *     range: a static collider exists only while the player is inside the
 *     ring (hysteresis so a stroll along the edge never thrashes).
 *   · settleStagesFromMarks — `performance.mark('settle:<site>:<stage>')`
 *     per build stage → a per-stage duration table; the last report is the
 *     perf HUD's "settle" row and the walk profile's SETTLE REPORT.
 */

/** Static colliders exist inside this walking range of the player … */
export const COLLIDER_RING_M = 320;
/** … and are dropped once the player is farther than this (hysteresis). */
export const COLLIDER_RING_DROP_M = 400;

export interface Placed { x: number; z: number; r: number }

/** A uniform grid over the XZ plane for footprint queries. */
export class SpatialHash2D<T extends Placed> {
  private readonly cells = new Map<number, T[]>();
  private maxR = 0;
  private count = 0;
  constructor(readonly cell = 48) {}
  private key(cx: number, cz: number): number { return (cx + 32768) * 65536 + (cz + 32768); }
  get size(): number { return this.count; }
  insert(item: T): void {
    const cx = Math.floor(item.x / this.cell), cz = Math.floor(item.z / this.cell);
    const k = this.key(cx, cz);
    const list = this.cells.get(k);
    if (list) list.push(item); else this.cells.set(k, [item]);
    if (item.r > this.maxR) this.maxR = item.r;
    this.count++;
  }
  /**
   * True when any item within `reach` + its own radius of (x, z) satisfies
   * `test` — the exact distance check stays in the caller's predicate, the
   * grid only bounds which items are worth testing.
   */
  anyWithin(x: number, z: number, reach: number, test: (item: T) => boolean): boolean {
    const R = reach + this.maxR;
    const cx0 = Math.floor((x - R) / this.cell), cx1 = Math.floor((x + R) / this.cell);
    const cz0 = Math.floor((z - R) / this.cell), cz1 = Math.floor((z + R) / this.cell);
    for (let cx = cx0; cx <= cx1; cx++) for (let cz = cz0; cz <= cz1; cz++) {
      const list = this.cells.get(this.key(cx, cz));
      if (!list) continue;
      for (const it of list) if (test(it)) return true;
    }
    return false;
  }
}

/** Should this collider exist right now? Inside the ring it is created; it lives until the drop radius. */
export function colliderRingWant(dist: number, active: boolean, ring = COLLIDER_RING_M, drop = COLLIDER_RING_DROP_M): boolean {
  return active ? dist <= drop : dist <= ring;
}

/**
 * The ring's ANCHOR is the building's FOOTPRINT (fx/fz — the plan's pad),
 * never its Rapier body origin (px/pz). The cooked civ GLBs are off-pivot
 * (bbox centers 40–1290 m from the model origin — the PIVOT LAW), so the
 * pivot-compensated body origin can sit a kilometre from the building it
 * carries: measuring the ring against it dropped every civ tower's collider
 * the tick after assembly added it (assembly measured the pad; the ring
 * measured the origin) and never re-added it — "structures have no
 * collision when walking, but a vehicle collides" (field report 2026-09-03;
 * the vehicle registry keys on the pad). Pure so the harness pins it.
 */
export function colliderRingWantFor(c: { fx: number; fz: number; active: boolean }, playerX: number, playerZ: number, ring = COLLIDER_RING_M, drop = COLLIDER_RING_DROP_M): boolean {
  return colliderRingWant(Math.hypot(c.fx - playerX, c.fz - playerZ), c.active, ring, drop);
}

/** The build stages in order (each mark ends the previous stage). */
export const SETTLE_STAGES = ['catalog', 'plan', 'resolve', 'load', 'warm', 'assemble', 'finish'] as const;

export interface SettleReport {
  siteId: number; tier: string;
  /** ms per stage (stage → duration until the next mark) */
  stages: Record<string, number>;
  totalMs: number;
  placements: number; colliders: number; collidersDeferred: number;
  overlapMs: number; colliderMs: number;
  at: number;
}

/** Stage durations for one site from performance marks named `settle:<siteId>:<stage>` (any order of input). */
export function settleStagesFromMarks(marks: ReadonlyArray<{ name: string; startTime: number }>, siteId: number): { stages: Record<string, number>; totalMs: number } {
  const t: Partial<Record<string, number>> = {};
  const prefix = `settle:${siteId}:`;
  for (const m of marks) if (m.name.startsWith(prefix)) t[m.name.slice(prefix.length)] = m.startTime; // last mark of a stage wins
  const stages: Record<string, number> = {};
  const present = SETTLE_STAGES.filter((s) => t[s] !== undefined);
  for (let i = 0; i + 1 < present.length; i++) stages[present[i]] = +(t[present[i + 1]]! - t[present[i]]!).toFixed(1);
  const totalMs = present.length >= 2 ? +(t[present[present.length - 1]]! - t[present[0]]!).toFixed(1) : 0;
  return { stages, totalMs };
}

/** The shared report store (the settlements module writes, the perf HUD + walk profile read). */
export const settleReports: { last: SettleReport | null; bySite: Map<number, SettleReport> } = { last: null, bySite: new Map() };

/** One-line summary for the HUD. */
export function settleLine(r: SettleReport | null): string {
  if (!r) return 'settle: —';
  const st = Object.entries(r.stages).map(([k, v]) => `${k} ${v >= 1000 ? `${(v / 1000).toFixed(1)}s` : `${Math.round(v)}ms`}`).join(' · ');
  return `settle #${r.siteId} ${r.tier}: ${(r.totalMs / 1000).toFixed(1)}s · ${st} · overlap ${r.overlapMs.toFixed(1)}ms · colliders ${r.colliders} now/${r.collidersDeferred} deferred ${r.colliderMs.toFixed(1)}ms`;
}
